import { Controller } from "@hotwired/stimulus"

// This controller is used to manage multi-select filters with checkboxes.
export default class extends Controller {
  static get targets() {
    return [
      "button",
      "optionElement",
      "optionLabel",
      "option",
      "selectedOption",
      "resultMetas",
      "selectedManyOptions",
      "selectedManyOptionsValue",
      "searchInput",
      "resultCount",
      "dataCount",
      "selectedIsPresent",
      "selectedIsEmpty",
    ]
  }

  static get values() {
    return {
      name: String,
      selected: { type: Array, default: [] },
      showAggregate: Boolean,
      optionsSize: Number,
      context: { type: String, default: "" },
    }
  }

  connect() {
    this.render()
  }

  open(event) {
    if (event.detail.name === this.nameValue) {
      // The dropdown won't be shown without the timeout
      setTimeout(() => {
        this.buttonTarget.click()
      }, 10)
    }
  }

  // Prevent dropdown from closing when typing in search
  preventClose(event) {
    event.stopPropagation()
  }

  search() {
    let query = this.searchInputTarget.value.toLowerCase()
    if (query.length === 0) {
      this.optionElementTargets.forEach((option) => {
        option.classList.remove("d-none")
      })
      this._updateMetas(this.optionsSizeValue, true)
      return
    }

    let optionSelector = this.targets.getSelectorForTargetName("option")
    let labelSelector = this.targets.getSelectorForTargetName("optionLabel")
    let resultCount = 0

    this.optionElementTargets.forEach((option) => {
      let input = option.querySelector(optionSelector)
      if (input.checked) {
        return
      } // We don't hide checked options regardless of search
      let labelText = option.querySelector(labelSelector).innerText.toLowerCase()

      if (labelText.includes(query)) {
        option.classList.remove("d-none")
        resultCount++
      } else {
        option.classList.add("d-none")
      }
    })

    this._updateMetas(resultCount)
  }

  apply() {
    let values = []
    this.optionTargets.forEach((option) => {
      if (option.checked) {
        values.push(option.value)
      }
    })

    this.selectedValue = values
    this.render()
    this.dispatchFilterEvent()

    if (this.hasButtonTarget) {
      const dropdown = bootstrap.Dropdown.getInstance(this.buttonTarget)
      dropdown.hide()
    }
  }

  remove(event) {
    let optionAsString = event.params.option.toString()
    this.selectedValue = this.selectedValue.filter((value) => value !== optionAsString)
    this.render()
    this.dispatchFilterEvent()
  }

  clear() {
    let anyChanges = this.selectedValue.length > 0
    this.selectedValue = []
    this.render()
    if (anyChanges) {
      this.dispatchFilterEvent()
    }
  }

  render() {
    let selectedOptions = 0
    this.optionTargets.forEach((option) => {
      if (!this.hasSelectedValue) {
        option.checked = false
        return
      }

      option.checked = this.selectedValue.includes(option.value)
      if (option.checked) {
        selectedOptions += 1
      }
    })

    if (this.hasDataCountTarget) {
      if (selectedOptions > 0) {
        this.dataCountTarget.dataset.count = selectedOptions
      } else {
        delete this.dataCountTarget.dataset.count
      }
    }

    if (this.hasButtonTarget) {
      this.buttonTarget.dataset.selectedFilters = this.selectedValue.length
    }

    this._toggleSelectedPresence(selectedOptions)
    this.showSelectedFilters()
  }

  showSelectedFilters() {
    if (this.showAggregateValue && this.selectedValue.length > 1) {
      // Too many filters selected, show composite box to remove them all
      if (this.hasSelectedManyOptionsTarget) {
        this.selectedManyOptionsTarget.classList.remove("d-none")
      }

      if (this.hasSelectedManyOptionsValueTarget) {
        this.selectedManyOptionsValueTarget.innerText = this.selectedValue.length
      }

      this.selectedOptionTargets.forEach((selectedOption) => {
        selectedOption.classList.add("d-none")
      })
      return
    }

    if (this.hasSelectedManyOptionsTarget) {
      this.selectedManyOptionsTarget.classList.add("d-none")
    }

    this.selectedOptionTargets.forEach((selectedOption) => {
      if (this.selectedValue.includes(selectedOption.dataset.value)) {
        selectedOption.classList.remove("d-none")
      } else {
        selectedOption.classList.add("d-none")
      }
    })
  }

  dispatchFilterEvent() {
    // Create event detail object
    const detail = {
      name: this.nameValue,
      values: this.selectedValue,
    }

    // Only add context if it's explicitly set (backwards compatibility)
    if (this.contextValue) {
      detail.context = this.contextValue
    }

    this.dispatch("filter", { detail })
  }

  _updateMetas(numberResults, showElements = false) {
    if (this.hasResultCountTarget) {
      this.resultCountTarget.textContent = i18n.t("js.back.filters.search_results_count", { count: numberResults })
    }

    if (showElements) {
      this.resultCountTarget.textContent = i18n.t("js.back.filters.elements_count", { count: numberResults })
    }
  }

  _toggleSelectedPresence(selectedCount) {
    const isPresenceSelected = this.selectedValue.includes("*")
    const isEmptySelected = this.selectedValue.includes("~")

    if (this.hasSelectedIsPresentTarget && selectedCount === 1) {
      this.selectedIsPresentTarget.classList.toggle("d-none", !isPresenceSelected)
    }

    if (this.hasSelectedIsEmptyTarget && selectedCount === 1) {
      this.selectedIsEmptyTarget.classList.toggle("d-none", !isEmptySelected)
    }
  }
}
